import * as React from 'react';
import * as _ from 'underscore';

import { BodyCell } from '@tableau/widgets-datagrid';

import { Messages } from '../../../../codegen/Localize';
import { AskDataMode } from '../../../../settings/types/AskDataMode';
import { ISiteSettingsForServerAdmin } from '../../../../settings/types/ISite';
import { ISiteAdminSettings, UserQuotaMode } from '../../../../settings/types/ISiteSettingsForSiteAdmin';
import { SizeFilter } from '../../../../ts/filters/SizeFilter';
import { L10n } from '../../../../ts/L10n/L10n';
import { IGroupInfo } from '../../../../ts/models/IGroupInfo';
import { ExtractEncryptionMode } from '../../../../ts/resources/Sites';
import { GrantLicenseModes } from '../../../../ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { DataGridCells } from '../../ListView/DataGridCells';

export namespace SiteGridCells {

  export function userQuotaFormatter(): React.StatelessComponent<BodyCell.Props> {
    const serverLimitElement = React.createElement('span', {}, Messages.serverLimit());
    return (props: BodyCell.Props) => {
      const site = props.row.data as ISiteSettingsForServerAdmin;
      if (site.settings.userQuotaMode === UserQuotaMode.SERVER_LIMIT) {
        return serverLimitElement;
      } else {
        return React.createElement('span', {}, L10n.localizedCount(site.settings.userQuota));
      }
    };
  }

  export function StorageUsedFormatter(props: BodyCell.Props): JSX.Element {
    const site = props.row.data as ISiteSettingsForServerAdmin;
    return React.createElement('span', {},
      site.settings.storageQuotaEnabled
        ? React.createElement('span', {}, '(' + L10n.localizedPercent(site.storageQuotaPercentUsed) + ') ')
        : null,
      React.createElement('span', {}, SizeFilter(site.storageUsedBytes))
    );
  }

  export function StorageQuotaFormatter(props: BodyCell.Props): JSX.Element {
    const serverLimitElement = React.createElement('span', {}, Messages.serverLimit());
    const site = props.row.data as ISiteSettingsForServerAdmin;
    if (site.settings.storageQuotaEnabled) {
      return React.createElement('span', {}, SizeFilter(site.settings.storageQuotaBytes));
    } else {
      return serverLimitElement;
    }
  }

  export function Checkmark(propertyName: string): React.StatelessComponent<BodyCell.Props> {
    const propertyNameParts = propertyName.split('.');
    return function CheckmarkCell(props: BodyCell.Props) {
      let value = props.row.data;
      _.each(propertyNameParts, (part: string) => {
        if (_.has(value, part)) {
          value = value[part];
        } else {
          value = null;
        }
      });

      return <span className={value ? 'tb-data-grid-icon tb-icon-checkmark' : null} />;
    };
  }

  export function LicenseOnSignInFormatter(props: BodyCell.Props): JSX.Element {
    let value = props.row.data as IGroupInfo;
    const isOnSignIn = (value.grantLicenseMode === GrantLicenseModes.ON_LOGIN);
    return isOnSignIn ? React.createElement('span',{ className: 'tb-data-grid-icon tb-icon-checkmark'}) : null;
  }

  export function ExtractEncryptionModeCell(): React.StatelessComponent<BodyCell.Props> {
    const siteStatusToMessageKey: _.Dictionary<string> = {};
    siteStatusToMessageKey[ExtractEncryptionMode.ENFORCED] = Messages.enforced();
    siteStatusToMessageKey[ExtractEncryptionMode.ENABLED] = Messages.enabled();
    siteStatusToMessageKey[ExtractEncryptionMode.DISABLED] = Messages.disabled();

    return DataGridCells.PlainTextCellWithTransform('siteAdminSettings',
      (siteAdminSettings: ISiteAdminSettings) => siteStatusToMessageKey[siteAdminSettings.extractEncryptionMode]
    );
  }

  export function AskDataModeCellFormatter(): React.StatelessComponent<BodyCell.Props> {
    const siteStatusToMessageKey: _.Dictionary<string> = {};
    siteStatusToMessageKey[AskDataMode.DISABLED_ALWAYS] = Messages.AskData_alwaysDisabled();
    siteStatusToMessageKey[AskDataMode.ENABLED_BY_DEFAULT] = Messages.AskData_enabledByDefault();
    siteStatusToMessageKey[AskDataMode.DISABLED_BY_DEFAULT] = Messages.AskData_disabledByDefault();

    return DataGridCells.PlainTextCellWithTransform('siteAdminSettings',
      (siteAdminSettings: ISiteAdminSettings) => siteStatusToMessageKey[siteAdminSettings.askDataMode]
    );
  }
}
